import type { PlaceAmenities, PlaceAmenityKey } from '../types'

type Amenity = { key: PlaceAmenityKey; label: string; hint: string }

// What the app's place section lists, with the question each answer settles,
// each group in the app's order. The first four keep a place pending until
// answered; the others only where they matter.
export const REQUIRED_AMENITIES: Amenity[] = [
  { key: 'is_accessible', label: 'Acessível', hint: 'Entra e chega ao espaço do evento sem degrau, ou com rampa ou elevador.' },
  { key: 'has_parking', label: 'Estacionamento', hint: 'Próprio ou conveniado.' },
  { key: 'pet_friendly', label: 'Aceita pets', hint: 'O local aceita animais.' },
  { key: 'is_indoor', label: 'Área coberta', hint: 'Se chover, o público tem onde ficar protegido.' },
]

export const OPTIONAL_AMENITIES: Amenity[] = [
  { key: 'has_bike_parking', label: 'Bicicletário', hint: 'Onde prender a bicicleta.' },
  { key: 'has_restroom', label: 'Banheiro', hint: 'Para o público. Confira primeiro nos locais ao ar livre.' },
  { key: 'is_air_conditioned', label: 'Ar-condicionado', hint: 'No espaço do evento.' },
  { key: 'has_smoking_area', label: 'Área para fumantes', hint: 'Um lugar dentro do local onde o público pode fumar: varanda, quintal ou área marcada.' },
]

// Food and drinks, three that don't exclude each other.
export const FOOD_AMENITIES: Amenity[] = [
  { key: 'has_restaurant', label: 'Restaurante', hint: 'Restaurante ou bistrô, com refeição completa.' },
  { key: 'has_cafe', label: 'Café ou lanchonete', hint: 'Café, lanchonete, cantina ou quiosque, para algo rápido.' },
  { key: 'has_bar', label: 'Bar', hint: 'Bar ou balcão vendendo bebidas ao público, como o de uma festa.' },
]

export const AMENITY_KEYS = [...REQUIRED_AMENITIES, ...FOOD_AMENITIES, ...OPTIONAL_AMENITIES].map((amenity) => amenity.key)

const ANSWERS: { value: boolean | null; label: string }[] = [
  { value: true, label: 'Sim' },
  { value: false, label: 'Não' },
  { value: null, label: 'Não conferido' },
]

export const isPending = (amenities: PlaceAmenities) =>
  REQUIRED_AMENITIES.some((amenity) => amenities[amenity.key] === null)

// Every amenity not checked yet, as a new place starts.
export const EMPTY_AMENITIES = Object.fromEntries(AMENITY_KEYS.map((key) => [key, null])) as PlaceAmenities

const GROUPS: [string, Amenity[]][] = [
  ['Para todo lugar', REQUIRED_AMENITIES],
  ['Comes e bebes', FOOD_AMENITIES],
  ['Quando fizer sentido', OPTIONAL_AMENITIES],
]

// What a place offers, yes, no or not checked, for each amenity: the Places
// list, the place's page and a place created inside an event use the same.
export function AmenityPicker({ name, value, disabled, onChange }: {
  name: string
  value: PlaceAmenities
  disabled?: boolean
  onChange: (value: PlaceAmenities) => void
}) {
  return (
    <>
      {GROUPS.map(([title, amenities]) => (
        <div className="amenity-group" key={title}>
          <h3>{title}</h3>
          <div className="amenity-grid">
            {amenities.map((amenity) => (
              <fieldset className="amenity-field" key={amenity.key} disabled={disabled}>
                <legend>{amenity.label}</legend>
                <div className="answer-toggle">
                  {ANSWERS.map((answer) => (
                    <label
                      key={answer.label}
                      className={[value[amenity.key] === answer.value && 'is-active', answer.value === null && 'is-unknown'].filter(Boolean).join(' ')}
                    >
                      <input
                        type="radio"
                        name={`${name}-${amenity.key}`}
                        checked={value[amenity.key] === answer.value}
                        onChange={() => onChange({ ...value, [amenity.key]: answer.value })}
                      />
                      {answer.label}
                    </label>
                  ))}
                </div>
                <small>{amenity.hint}</small>
              </fieldset>
            ))}
          </div>
        </div>
      ))}
    </>
  )
}
